import React from 'react';
import { ShieldCheck, Phone, ArrowRight, AlertTriangle, BookOpen, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const PTSDSupportPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="PTSD Support & Treatment in Maryland"
        description="PTSD treatment and trauma support in Pikesville and Baltimore County, MD. SDORA Wellbeing offers outpatient therapy, medication management, and rehabilitation for veterans and adults living with PTSD. Accepting Medicaid."
        path="/resources/ptsd-support"
        type="article"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-700 to-gray-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShieldCheck className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            PTSD Support & Treatment in Maryland
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Trauma does not have to control your life. SDORA Wellbeing provides compassionate, evidence-based support for adults and veterans living with PTSD.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Understanding PTSD */}
        <section className="mb-12" data-testid="ptsd-understanding-section">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Understanding PTSD</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Post-Traumatic Stress Disorder (PTSD) is a mental health condition that can develop after experiencing or witnessing
            a traumatic event. This can include combat, assault, accidents, natural disasters, abuse, or any event where you
            felt your life or safety was in danger.
          </p>
          <p className="text-gray-700 leading-relaxed">
            PTSD is not a sign of weakness. It is a normal response to abnormal circumstances, and it can affect anyone regardless
            of age, background, or strength. With proper treatment, many people with PTSD experience significant improvement
            in their symptoms and quality of life.
          </p>
        </section>

        {/* Symptoms */}
        <section className="mb-12 bg-amber-50 rounded-xl p-6 border border-amber-100" data-testid="ptsd-symptoms">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Common Symptoms of PTSD</h2>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            PTSD symptoms typically fall into four categories. They may begin within months of a traumatic event or appear years later:
          </p>
          <div className="space-y-4">
            {[
              {
                category: "Re-experiencing",
                symptoms: "Flashbacks, nightmares, intrusive memories of the event, emotional or physical reactions to reminders.",
              },
              {
                category: "Avoidance",
                symptoms: "Avoiding places, people, activities, or thoughts that remind you of the trauma. Emotional numbing or feeling detached.",
              },
              {
                category: "Negative Changes in Thinking & Mood",
                symptoms: "Persistent negative beliefs about yourself or the world, guilt or blame, difficulty feeling positive emotions, loss of interest.",
              },
              {
                category: "Hyperarousal",
                symptoms: "Being easily startled, feeling on guard, irritability, difficulty sleeping, trouble concentrating, reckless or self-destructive behavior.",
              },
            ].map((group) => (
              <div key={group.category}>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{group.category}</h3>
                <p className="text-sm text-gray-600">{group.symptoms}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Treatment Options */}
        <section className="mb-12" data-testid="ptsd-treatments">
          <h2 className="text-lg font-bold text-gray-900 mb-4">How SDORA Wellbeing Supports PTSD Recovery</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Our licensed clinicians understand that trauma recovery is deeply personal. We work at your pace to create a treatment
            plan that feels safe and manageable, using evidence-based approaches tailored to your specific experiences and goals.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Trauma-Focused Therapy (OMHC)",
                desc: "Individual therapy sessions using evidence-based approaches to help you process traumatic experiences, reduce distress, and develop healthy coping strategies.",
                link: "/services/outpatient-mental-health",
              },
              {
                title: "Medication Management",
                desc: "Psychiatric evaluation and medication support for symptoms like insomnia, anxiety, depression, or hyperarousal that often accompany PTSD.",
                link: "/services/medication-management",
              },
              {
                title: "Psychiatric Rehabilitation (PRP)",
                desc: "Practical skill-building support to help you manage triggers, rebuild daily routines, improve relationships, and re-engage with your community.",
                link: "/services/psychiatric-rehabilitation",
              },
              {
                title: "Case Management",
                desc: "Coordinated support connecting you to housing, benefits, veterans resources, and other services that contribute to stability and recovery.",
                link: "/services/case-management",
              },
            ].map((item) => (
              <div key={item.title} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-900 text-sm mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{item.desc}</p>
                <Link to={item.link} className="text-sm text-slate-700 font-medium hover:underline inline-flex items-center gap-1">
                  Learn more <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Veteran Support */}
        <section className="mb-12 bg-slate-50 rounded-xl p-6 border border-slate-200" data-testid="ptsd-veteran-section">
          <div className="flex items-start gap-3 mb-3">
            <Users className="w-5 h-5 text-slate-700 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Support for Veterans</h2>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            PTSD affects a significant number of veterans who have served in combat or experienced military-related trauma.
            SDORA Wellbeing provides culturally informed care that respects the unique experiences and challenges of veteran life.
          </p>
          <p className="text-sm text-gray-700 mb-3">
            Our veteran services include mental health therapy, case management, and community reintegration support.
            We can also help you navigate VA benefits and connect with veteran-specific resources in the Baltimore area.
          </p>
          <Link to="/services/veteran-services" className="text-sm text-slate-700 font-semibold hover:underline inline-flex items-center gap-1">
            Learn about our Veteran Services <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* Telehealth */}
        <section className="mb-12 bg-blue-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start gap-3 mb-3">
            <BookOpen className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Telehealth Available</h2>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            For many people with PTSD, leaving home or visiting a clinical setting can feel overwhelming. Telehealth allows
            you to receive therapy and medication management from a safe, familiar environment.
          </p>
          <Link to="/services/telehealth" className="text-sm text-blue-700 font-semibold hover:underline inline-flex items-center gap-1">
            Learn about telehealth options <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* Crisis Resources */}
        <section className="mb-12 bg-red-50 rounded-xl p-6 border border-red-100" data-testid="ptsd-crisis-resources">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Crisis Resources</h2>
          <div className="space-y-2 text-sm text-gray-700">
            <p><strong>Suicide & Crisis Lifeline:</strong> Call or text <strong>988</strong> (available 24/7)</p>
            <p><strong>Veterans Crisis Line:</strong> Call <strong>988</strong>, then press <strong>1</strong></p>
            <p><strong>Crisis Text Line:</strong> Text <strong>HOME</strong> to <strong>741741</strong></p>
            <p className="pt-2 text-gray-600">
              If you are in immediate danger, call 911 or go to your nearest emergency room.
            </p>
          </div>
        </section>

        {/* Insurance */}
        <section className="mb-12 bg-slate-50 rounded-xl p-6 border border-slate-100">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Insurance & Coverage</h2>
          <p className="text-sm text-gray-700 mb-3">
            SDORA Wellbeing accepts Medicaid, Maryland HealthChoice managed care organizations, and most major insurance plans
            for PTSD treatment. Contact us to verify your specific coverage.
          </p>
          <Link to="/insurance" className="text-sm text-slate-700 font-semibold hover:underline inline-flex items-center gap-1">
            View accepted insurance plans <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* CTA */}
        <section className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200" data-testid="ptsd-cta">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Begin Your Healing Journey</h2>
          <p className="text-sm text-gray-600 mb-4">
            Recovery from PTSD is possible. Call us today or submit a referral — our team is ready to support you at your pace.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+18777725150" className="inline-flex items-center gap-2 bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <Link to="/get-started" className="inline-flex items-center gap-2 bg-white text-slate-700 border border-slate-300 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50 transition-colors">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default PTSDSupportPage;
